/* eslint-disable tsdoc/syntax */
import { CompletionContext } from '@codemirror/autocomplete';
import { EditorState } from '@codemirror/state';

import { verifyQuery } from './dql-linter.js';

/**
 * Checks whether the cursor is at a position where a command or parameter is expected,
 * following a comma or pipe separator, with only whitespace in between.
 * @param context - the current completion context
 * @returns true if the cursor is at the start of a command or parameter, false otherwise
 */
function isStartOfCommandOrParameter(context: CompletionContext): boolean {
  return context.matchBefore(/[,|]\s+$/) !== null;
}

/**
 * Checks whether the user is typing a command prefix (e.g. `d`, `da`, `fet`)
 * at a valid command boundary: start of query or after a `|` or `,` separator.
 * Unlike isStartOfCommandOrParameter, this also matches once characters have
 * been typed after the separator (e.g. `fetch logs | d`, `fetch logs | de`),
 * which is needed for reopening suggestions after ESC and continued typing.
 * @param context - the current completion context
 * @returns true if the user is typing a command prefix at a valid command boundary, false otherwise
 */
function isTypingCommandPrefixAtCommandBoundary(
  context: CompletionContext,
): boolean {
  return context.matchBefore(/(?:^|[|,]\s*)[A-Za-z][A-Za-z0-9]*$/) !== null;
}

/**
 * Checks whether the user is typing a parameter prefix after a command.
 * This covers reopening suggestions after ESC when typing continues within
 * the first parameter token at query start, for example
 * `fetch app` -> `fetch appl`.
 * @param context - the current completion context
 * @returns true if the user is typing a parameter prefix after a command, false otherwise
 */
function isTypingParameterPrefixAfterCommand(
  context: CompletionContext,
): boolean {
  const commandAndParameter = context.matchBefore(
    /^[A-Za-z][A-Za-z0-9]*\s+[A-Za-z_][A-Za-z0-9_.]*\s*$/,
  );

  return commandAndParameter !== null;
}

/**
 * Allows opening autocomplete when only whitespace, a comment, or no more
 * content follows the cursor. This covers typing before comments and reopening
 * suggestions after ESC when the user continues typing at the end of the query.
 */
function isBeforeCommentOrEndOfContent(state: EditorState): boolean {
  const afterCursor = state.doc.slice(state.selection.main.to).toString();
  const afterCursorTrimmed = afterCursor.trimStart();

  return (
    afterCursorTrimmed === '' ||
    afterCursorTrimmed.startsWith('//') ||
    afterCursorTrimmed.startsWith('/*')
  );
}

/**
 * Checks whether the query is invalid at the end.
 *
 * @param query - The query string to check
 * @returns A promise that resolves to true if the query is invalid at the end, false otherwise
 */
export async function isInvalidAtEnd(query: string): Promise<boolean> {
  const trimmedQuery = query.trimEnd();
  const { valid = true, notifications = [] } =
    (await verifyQuery(trimmedQuery)) ?? {};

  const hasErrorNotificationAtEnd = notifications?.some(
    (notification) =>
      notification.severity === 'ERROR' &&
      notification.syntaxPosition?.end.index === trimmedQuery.length - 1,
  );

  return !valid && hasErrorNotificationAtEnd;
}

/**
 * Determines if the autocomplete dropdown should be opened.
 *
 * @param update - Information on the current changes from the update listener
 * @param state - EditorState
 * @returns true when the autocomplete should be opened
 */
export async function openAutocomplete(
  update: {
    isTyping: boolean;
    isDeleting: boolean;
    isFocused: boolean;
    selectionChanged: boolean;
  },
  state: EditorState,
): Promise<boolean> {
  const { isTyping, isDeleting, isFocused, selectionChanged } = update;

  const context = new CompletionContext(state, state.selection.main.to, false);

  if (
    isTyping &&
    (isTypingCommandPrefixAtCommandBoundary(context) ||
      isTypingParameterPrefixAfterCommand(context)) &&
    isBeforeCommentOrEndOfContent(state)
  ) {
    return true;
  }

  // open autocomplete if
  //  - content is empty
  //  - the cursor is at the very end following a comma or pipe
  //  - the cursor is on a fresh line after existing content
  if (selectionChanged || isFocused) {
    const fullContent = state.doc.toString();
    const cursorAtTheEnd = fullContent.length === state.selection.ranges[0].to;
    const cursorPosition = state.selection.main.to;
    const textBeforeCursor = state.doc.slice(0, cursorPosition).toString();
    const isOnFreshLine =
      context.matchBefore(/^\s*$/) !== null && textBeforeCursor.trim() !== '';

    if (
      fullContent.trim() === '' ||
      (cursorAtTheEnd && isStartOfCommandOrParameter(context)) ||
      isOnFreshLine
    ) {
      return true;
    }
  }

  const hasWhitespaceBefore = context.matchBefore(/\s/);

  // open autocomplete when the user is typing following a command (==keyword)
  if (
    (isTyping || isDeleting) &&
    context.tokenBefore(['keyword']) &&
    hasWhitespaceBefore
  ) {
    return true;
  }

  // open autocomplete if the user is typing following a comma or pipe
  if (isTyping && isStartOfCommandOrParameter(context)) {
    return true;
  }

  // open autocomplete if query is invalid and a space was typed beforehand
  if (isTyping && hasWhitespaceBefore) {
    const query = state.doc.slice(0, state.selection.main.to).toString();
    if (await isInvalidAtEnd(query)) {
      return true;
    }
  }

  const characterAfter = state.doc
    .slice(state.selection.main.to, state.selection.main.to + 1)
    .toString();
  const hasSpaceOrEndOfContentAfter = characterAfter.trim().length === 0;

  // open autocomplete if
  //  - a whitespace was deleted
  //  - the last char was deleted
  if (isDeleting && hasSpaceOrEndOfContentAfter) {
    return true;
  }

  return false;
}
